import translations from '../../../translations';
import Toggle from '../../inputs/Toggle';
import type { ChangeEventHandler, ComponentProps } from 'react';
import FormGroup from '../../generic/FormGroup';
import type { CookieType, SamlSettings } from '../../../types/Tenant';
import type { UpdateTenant } from '../TenantPage';

const cookieType: Record<CookieType, CookieType> = {
    persistent: 'persistent',
    transient: 'transient',
};

type SAMLCookieSettingsProps = {
    samlSettings: SamlSettings | null | undefined;
    updateTenant: UpdateTenant;
};

const assertCookieType = (value: string): value is CookieType => {
    return value === cookieType.persistent || value === cookieType.transient;
};

const SAMLCookieSettings = ({ samlSettings, updateTenant }: SAMLCookieSettingsProps) => {
    const onChangeCookieType: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) => {
        if (!assertCookieType(value)) {
            throw new Error(
                `Invalid cookie type ${value}. Type must be ${cookieType.persistent} or ${cookieType.transient}`,
            );
        }

        updateTenant({
            securitySettings: {
                samlSettings: {
                    cookieType: value,
                },
            },
        });
    };

    const onChangeMaxSession: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        updateTenant({
            securitySettings: {
                samlSettings: {
                    maxSession: value ? Number.parseInt(value) : null,
                },
            },
        });
    };

    const onChangeIdleTimeout: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        updateTenant({
            securitySettings: {
                samlSettings: {
                    idleTimeout: value ? Number.parseInt(value) : null,
                },
            },
        });
    };

    const onChangeShowUri: ComponentProps<typeof Toggle>['onChange'] = ({ isOn }) => {
        updateTenant({
            securitySettings: {
                samlSettings: {
                    showUri: isOn,
                },
            },
        });
    };

    return (
        <>
            <FormGroup htmlFor="saml-cookie-type" label={translations.SAML_cookietype_label}>
                <select
                    id="saml-cookie-type"
                    value={samlSettings?.cookieType ?? cookieType.persistent}
                    onChange={onChangeCookieType}
                    className="form-control"
                >
                    <option value={cookieType.persistent}>
                        {translations.SAML_cookietype_persistent}
                    </option>
                    <option value={cookieType.transient}>
                        {translations.SAML_cookietype_transient}
                    </option>
                </select>
                <p className="help-block">{translations.SAML_cookietype_help}</p>
            </FormGroup>
            <FormGroup htmlFor="saml-max-session" label={translations.SAML_max_session_label}>
                <input
                    id="saml-max-session"
                    type="number"
                    value={samlSettings?.maxSession ?? ''}
                    placeholder={translations.SAML_max_session_placeholder}
                    onChange={onChangeMaxSession}
                    className="form-control"
                />
                <p className="help-block">{translations.SAML_max_session_help}</p>
            </FormGroup>
            <FormGroup
                htmlFor="saml-enable-show-url-toggle"
                label={translations.SAML_max_session_show_uri}
                labelPosition="after"
            >
                <Toggle
                    className="saml-enable-show-url"
                    isOn={samlSettings?.showUri ?? false}
                    onChange={onChangeShowUri}
                    id="saml-enable-show-url-toggle"
                    testId="saml-enable-show-url-toggle"
                />
                <p className="help-block">{translations.SAML_max_session_show_uri_help}</p>
            </FormGroup>
            <FormGroup htmlFor="saml-idle-timeout" label={translations.SAML_idle_timeout}>
                <input
                    id="saml-idle-timeout"
                    type="number"
                    value={samlSettings?.idleTimeout ?? ''}
                    placeholder={translations.SAML_idle_timeout_placeholder}
                    onChange={onChangeIdleTimeout}
                    className="form-control"
                />
                <p className="help-block">{translations.SAML_idle_timeout_help}</p>
            </FormGroup>
        </>
    );
};

export default SAMLCookieSettings;
